import type { ReactNode } from 'react';
import type { PaymentSchedule } from '@/api/schemas';
import { useClock } from '@/lib/clock';
import { formatMoney, NOT_AVAILABLE } from '@/lib/display';
import { DateText } from '@/lib/formatDate';
import { nextPayment, paymentCommission } from '@/lib/payments';

type Tone = 'customer' | 'dealer';

const TONES: Record<Tone, { panel: string; title: string }> = {
  customer: {
    panel: 'bg-[var(--customer-surface)]',
    title: 'text-[var(--customer-ink)]',
  },
  dealer: {
    panel: 'bg-[var(--dealer-surface)]',
    title: 'text-[var(--dealer-ink)]',
  },
};

type Figure = { label: string; value: string; caption?: ReactNode };

function Overview({
  title,
  tone,
  figures,
}: {
  title: string;
  tone: Tone;
  figures: readonly Figure[];
}) {
  return (
    <section
      aria-label={title}
      className={`rounded-[4px] px-4 py-5 ${TONES[tone].panel}`}
    >
      <h2
        className={`text-center text-[16px] font-medium uppercase leading-5 ${TONES[tone].title}`}
      >
        {title}
      </h2>
      <div className="mt-5 grid grid-cols-2 gap-4 text-center sm:grid-cols-4">
        {figures.map((figure) => (
          <div key={figure.label}>
            <p className="text-[14px] leading-5 text-black">{figure.label}</p>
            <p className="mt-2 text-[20px] font-medium leading-6 text-black">
              {figure.value}
            </p>
            {figure.caption ? (
              <p className="mt-1 text-[12px] leading-4 text-[rgba(0,0,0,0.6)]">
                {figure.caption}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

export function PaymentOverviews({ schedule }: { schedule: PaymentSchedule }) {
  const customer = schedule.customerPayments;
  const dealer = schedule.dealerCommissions;
  const next = nextPayment(schedule.payments, useClock().today());
  const nextCaption = next ? (
    <>
      P# {next.number} due on <DateText value={next.dueDate} />
    </>
  ) : undefined;
  const remainingCaption = `${customer.paymentsRemaining} payments remaining out of ${schedule.payments.length}`;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Overview
        title="Customer Payments overview"
        tone="customer"
        figures={[
          {
            label: 'Contracts Total',
            value: formatMoney(customer.contractsTotal),
          },
          {
            label: 'Next Payment',
            value: next ? formatMoney(next.amount) : NOT_AVAILABLE,
            caption: nextCaption,
          },
          { label: 'Customer Paid', value: formatMoney(customer.paid) },
          {
            label: 'Remaining',
            value: formatMoney(customer.remaining),
            caption: remainingCaption,
          },
        ]}
      />
      <Overview
        title="Dealer Commissions overview"
        tone="dealer"
        figures={[
          { label: 'Total Commissions', value: formatMoney(dealer.total) },
          {
            label: 'Next Comm. Payment',
            value: next ? formatMoney(paymentCommission(next)) : NOT_AVAILABLE,
            caption: nextCaption,
          },
          { label: 'Credited to Dealer', value: formatMoney(dealer.credited) },
          {
            label: 'Remaining Comm.',
            value: formatMoney(dealer.remaining),
            caption: remainingCaption,
          },
        ]}
      />
    </div>
  );
}
